import { decodeToFrames, useAudioEngine } from '@tunasong/audio-ui'
import type { CaptureDiagnosis, MelodyLib } from '@tunasong/music-lib'
import { melodyGrids } from '@tunasong/schemas'
import type { Melody, MelodyGrid, MelodyMeter } from '@tunasong/schemas'
import { Button, Dialog, Select } from '@tunasong/ui-lib'
import { useRef, useState } from 'react'
import type { ChangeEvent, FC } from 'react'
import { CaptureReview } from './capture-review.js'
import styles from './extract-melody-dialog.module.css'
import { MIN_TAPS, tempoFromTaps, type TappedTempo } from './tap-downbeat.js'
import { framesToRawNotes } from './use-audio-capture.js'

/**
 * "Extract melody" - the May SPEC's third entry point, on a clip that already exists.
 *
 * The clip was never performed to a click, so v1 asks the user for the two numbers the grid needs
 * and automatic estimation would have to guess (§5; estimation is v2): the tempo, and where beat 1
 * falls IN THE CLIP.
 *
 * Both come from the same taps, and the taps are read off the CLIP's playhead rather than the wall
 * clock. That is what makes the downbeat meaningful: `currentTime` at the first tap is a position in
 * the recording, which is exactly what the frames are timed from, so the downbeat is t0 with no
 * conversion. Tapping against a paused clip yields no tempo at all, which is correct - there is
 * nothing to tap along to.
 *
 * The decode runs offline at the worklet's own window and hop, through `decodeToFrames`, and then
 * through the same voicing gate, segmenter, velocity mapping and quantizer the live path uses. A
 * melody extracted from a clip and the same melody sung live cannot disagree about the pitch layer
 * because they are the same code (§8).
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

const GRID_LABEL: Record<MelodyGrid, string> = {
  '8': '1/8',
  '16': '1/16',
  '8t': '1/8 triplet',
  '16t': '1/16 triplet',
}

const BEATS_PER_BAR = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

/** A tap run is finished when the user stops; a fresh tap after this long starts a new count. */
const TAP_RESET_MS = 3000

export interface ExtractMelodyDialogProps {
  /** The clip's playable URL. Fetched and decoded once, when Extract is pressed. */
  url: string
  /** The audio entity the notes came from - it becomes `source.entityId`. */
  clipId: string
  clipName?: string
  open: boolean
  onExtract: (melody: Melody) => void
  onClose: () => void
}

export const ExtractMelodyDialog: FC<ExtractMelodyDialogProps> = props => {
  const { url, clipId, clipName, open, onExtract, onClose } = props
  const engine = useAudioEngine()

  const audioRef = useRef<HTMLAudioElement>(null)
  const taps = useRef<number[]>([])

  const [tapped, setTapped] = useState<TappedTempo | null>(null)
  const [meter, setMeter] = useState<MelodyMeter>({ top: 4, bottom: 4 })
  const [grid, setGrid] = useState<MelodyGrid>('8')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ raw: MelodyLib.RawNote[]; diagnosis: CaptureDiagnosis } | null>(null)

  const handleTap = () => {
    /** The clip's own clock. Milliseconds, so the taps and the frame times are the same units. */
    const at = (audioRef.current?.currentTime ?? 0) * 1000
    const previous = taps.current[taps.current.length - 1]
    taps.current = previous !== undefined && at - previous > TAP_RESET_MS ? [at] : [...taps.current, at]
    setTapped(tempoFromTaps(taps.current))
  }

  const handleExtract = async () => {
    if (!tapped) {
      return
    }
    setBusy(true)
    try {
      const response = await fetch(url)
      const buffer = await engine.mixer.context.decodeAudioData(await response.arrayBuffer())
      /*
       * The downbeat is where beat 1 sits in the clip, and the frames are timed from the clip's
       * start - so the downbeat IS t0, with nothing to convert.
       */
      setResult(framesToRawNotes(decodeToFrames(buffer), tapped.downbeatMs))
    } finally {
      setBusy(false)
    }
  }

  const handleMeterTop = (event: ChangeEvent<HTMLSelectElement>) =>
    setMeter(current => ({ ...current, top: Number(event.target.value) }))

  const handleMeterBottom = (event: ChangeEvent<HTMLSelectElement>) =>
    setMeter(current => ({ ...current, bottom: event.target.value === '8' ? 8 : 4 }))

  const handleClose = () => {
    setResult(null)
    onClose()
  }

  const handleAccept = (melody: Melody) => {
    onExtract(melody)
    setResult(null)
    onClose()
  }

  const extracted: Melody | null = tapped
    ? {
        type: 'melody',
        name: clipName ? `${clipName} melody` : 'Extracted melody',
        tempo: { bpm: tapped.bpm, meter },
        grid,
        notes: [],
        source: { kind: 'audio', entityId: clipId },
      }
    : null

  return (
    <Dialog open={open} onClose={handleClose} title="Extract melody" fullWidth={true} maxWidth="md">
      <div className={styles.root}>
        {result && extracted ? (
          <CaptureReview
            melody={extracted}
            raw={result.raw}
            source={{ kind: 'audio', entityId: clipId }}
            diagnosis={result.diagnosis}
            onAccept={handleAccept}
            onRetake={() => setResult(null)}
            onCancel={handleClose}
          />
        ) : (
          <>
            {/* A music clip carries no captions; the control is here to play, not to read. */}
            <audio ref={audioRef} className={styles.player} data-testid="clip-audio" src={url} controls={true} />

            <span className={styles.instructions}>
              Play the clip and tap the beat, starting on the first downbeat of the part you want. Four taps
              minimum.
            </span>

            <div className={styles.controls}>
              <Button onClick={handleTap} aria-label="Tap">
                Tap
              </Button>

              <Select className={styles.control} label="Beats per bar" value={meter.top} onChange={handleMeterTop}>
                {BEATS_PER_BAR.map(top => (
                  <option key={top} value={top}>
                    {top}
                  </option>
                ))}
              </Select>

              <Select className={styles.control} label="Beat unit" value={meter.bottom} onChange={handleMeterBottom}>
                <option value={4}>4</option>
                <option value={8}>8</option>
              </Select>

              <Select
                className={styles.control}
                label="Grid"
                value={grid}
                onChange={event => setGrid(event.target.value as MelodyGrid)}
              >
                {melodyGrids.map(value => (
                  <option key={value} value={value}>
                    {GRID_LABEL[value]}
                  </option>
                ))}
              </Select>
            </div>

            <span className={styles.readout} role="status">
              {tapped
                ? `${tapped.bpm} BPM · downbeat at ${(tapped.downbeatMs / 1000).toFixed(1)} s`
                : `Tap at least ${MIN_TAPS} times while the clip plays.`}
            </span>

            <div className={styles.actions}>
              <Button disabled={!tapped || busy} onClick={handleExtract}>
                {busy ? 'Listening to the clip...' : 'Extract'}
              </Button>
              <Button onClick={handleClose}>Cancel</Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
